import type { AxiosResponse } from 'axios';
import { omit } from 'lodash';
import { useFlag } from '@loadsmart/vue-feature-flags';
import type { IAppointment, IWarehouse } from '@nova/core';

import axiosClient from '@/api/axiosClient';
import useRecaptcha from '@/components/composables/useRecaptcha';

export type CheckinPayload = Record<string, unknown>;

export type CompanyAutocompleteItem = {
  id: string;
  name: string;
  scac?: string;
  [key: string]: unknown;
};

export type CompanyAutocompleteResponse = {
  items: CompanyAutocompleteItem[];
  meta: { totalRecords: number; page: number };
};

export type CompanyAutocompletePayload = {
  search: string;
  pagination: { limit: number; offset: number };
};

export type UploadDocumentParams = {
  warehouseId: string;
  appointmentIdentifier?: string;
  file: File;
};

export type UploadDocumentResponse = { url: string; key: string };

export type RequestOptions = { signal?: AbortSignal };

function unwrap<T>(res: AxiosResponse): T {
  return (res.data?.data ?? res.data) as T;
}

export default function cometApiService() {
  const bffFlag = useFlag('enable-comet-bff');
  const { getToken } = useRecaptcha();

  const isBffEnabled = (): boolean => Boolean(bffFlag.value?.enabled);

  async function recaptchaHeaders(action: string): Promise<Record<string, string>> {
    const token = await getToken(action);
    return { 'X-Recaptcha-Token': token };
  }

  return {
    async getWarehouse(warehouseId: string): Promise<Partial<IWarehouse>> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_initial');
        const res = await axiosClient.get(`/bff/comet/${warehouseId}/initial`, { headers });
        return unwrap(res);
      }
      const res = await axiosClient.get(`/checkin/public/${warehouseId}/`);
      return unwrap(res);
    },

    async companyAutocomplete(
      warehouseId: string,
      payload: CompanyAutocompletePayload,
      options?: RequestOptions
    ): Promise<CompanyAutocompleteResponse> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_company_autocomplete');
        const res = await axiosClient.post(
          `/bff/comet/${warehouseId}/company-autocomplete`,
          payload,
          { headers, signal: options?.signal }
        );
        return res.data;
      }
      const res = await axiosClient.post(
        `/checkin/public/${warehouseId}/company-autocomplete`,
        payload,
        { signal: options?.signal }
      );
      return res.data;
    },

    async getAppointment(
      warehouseId: string,
      identifier: string,
      geo: { latitude?: number; longitude?: number } = {}
    ): Promise<IAppointment> {
      const encoded = encodeURIComponent(identifier);
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_appointment_lookup');
        const res = await axiosClient.get(`/bff/comet/${warehouseId}/appointment/${encoded}`, {
          headers
        });
        return unwrap(res);
      }
      const res = await axiosClient.get(`/checkin/public/${encoded}/${warehouseId}/`, {
        params: {
          latitude: Number.isFinite(geo.latitude) ? geo.latitude : '',
          longitude: Number.isFinite(geo.longitude) ? geo.longitude : ''
        }
      });
      return unwrap(res);
    },

    async submitPlanned<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      const body = {
        ...omit(payload, ['warehouseId']),
        assetDetails: omit((payload.assetDetails ?? {}) as Record<string, unknown>, [
          'companyId',
          'companyHint',
          'driverNotes'
        ])
      };
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_checkin_planned');
        const res = await axiosClient.put(`/bff/comet/${warehouseId}`, body, { headers });
        return unwrap(res);
      }
      const res = await axiosClient.put(`/checkin/public/${warehouseId}`, body);
      return unwrap(res);
    },

    async submitUnplanned<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      const body = omit(payload, ['warehouseId', 'appointmentId', 'notes']);
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_checkin_unplanned');
        const res = await axiosClient.put(`/bff/comet/${warehouseId}/unplanned`, body, { headers });
        return unwrap(res);
      }
      // The legacy route's CustomFormsTriggerInterceptor resolves the warehouse from
      // request.body.warehouseId (not the URL param) to look up custom-form triggers,
      // so it must stay in the body here even though the BFF DTO forbids it.
      const res = await axiosClient.put(`/checkin/public/${warehouseId}/unplanned`, {
        ...body,
        warehouseId
      });
      return unwrap(res);
    },

    async rejectCheckin<T = CheckinPayload>(
      warehouseId: string,
      payload: CheckinPayload
    ): Promise<T> {
      if (isBffEnabled()) {
        const headers = await recaptchaHeaders('comet_checkin_rejected');
        const res = await axiosClient.put(`/bff/comet/${warehouseId}/reject`, payload, { headers });
        return unwrap(res);
      }
      const res = await axiosClient.put(`/checkin/public/${warehouseId}/reject`, payload);
      return unwrap(res);
    },

    async uploadDocument({
      warehouseId,
      appointmentIdentifier,
      file
    }: UploadDocumentParams): Promise<UploadDocumentResponse> {
      const formData = new FormData();
      formData.append('file', file);
      const url = appointmentIdentifier
        ? `checkin/public/storage/${encodeURIComponent(appointmentIdentifier)}/${warehouseId}`
        : `checkin/public/storage/${warehouseId}`;
      const res = await axiosClient.post(url, formData);
      return res.data;
    },

    async smartGateAutocomplete(
      warehouseId: string,
      options?: RequestOptions
    ): Promise<Record<string, unknown>[]> {
      const res = await axiosClient.get(`/observations/public/${warehouseId}/autocomplete`, {
        signal: options?.signal
      });
      return res.data ?? [];
    }
  };
}
